import { useEffect, useRef, useState } from 'react';
import {
  createScreenistaMarkerIcon,
  getMarkerIconConfig,
  loadGoogleMapsScript,
} from '@/shared/lib/maps/googleMaps';
import { getGoogleMapsApiKey } from '@/features/billboards/utils/billboardDetailUtils';

type ScreenMapProps = {
  lat?: number;
  lng?: number;
  title?: string;
};

export function ScreenMap({ lat, lng, title }: ScreenMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    if (!hasCoords) {
      setError(true);
      return;
    }

    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      setError(true);
      return;
    }

    let marker: google.maps.Marker | undefined;
    let isMounted = true;

    loadGoogleMapsScript(apiKey)
      .then(async (googleMaps) => {
        if (!mapRef.current || !isMounted) return;

        const iconUrl = await createScreenistaMarkerIcon();
        const position = { lat: lat as number, lng: lng as number };

        const map = new googleMaps.Map(mapRef.current, {
          center: position,
          zoom: 15,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
        });

        marker = new googleMaps.Marker({
          position,
          map,
          title: title || 'Screen location',
          icon: getMarkerIconConfig(googleMaps, iconUrl),
        });
      })
      .catch(() => {
        if (isMounted) setError(true);
      });

    return () => {
      isMounted = false;
      marker?.setMap(null);
    };
  }, [hasCoords, lat, lng, title]);

  /* Modified by: Ishan | Soft-contrast map container */
  if (error || !hasCoords) {
    return (
      <div className="flex h-[260px] w-full items-center justify-center rounded-[24px] border border-[#EEEBF8] bg-[#F3F2F9] text-sm font-medium text-[#6A6688]">
        Map unavailable for this location
      </div>
    );
  }

  return (
    <div className="h-[260px] w-full overflow-hidden rounded-[24px] border border-[#EEEBF8] bg-[#F3F2F9]">
      <div ref={mapRef} className="h-full w-full" />
    </div>
  );
}
